import { ListSubheader, useMediaQuery, useTheme } from '@mui/material';
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import {
  Children,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { flushSync } from 'react-dom';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

/*
 * A react-window listbox for MUI Autocomplete, for option lists long enough that mounting every
 * option is what makes the dropdown slow (an organisation with thousands of suppliers). Pass it as
 * `ListboxComponent`. The Autocomplete keeps its own filtering, keyboard handling and styling;
 * only the options in view (plus a few either side) are mounted.
 *
 * - Rows have a fixed height. VirtualizedListbox uses the height MUI gives a plain one-line option
 *   (36px, 48px on phones), so the dropdown looks the same as an ordinary one. Options that are
 *   taller (a checkbox, a second line of text) need a listbox made for their height with
 *   createVirtualizedListbox, e.g. CheckboxVirtualizedListbox. Labels are kept to one line.
 * - The list is as tall as MUI's own listbox: at most 40% of the viewport.
 * - Keyboard and autoHighlight: MUI moves its highlight only between mounted options (it treats an
 *   unmounted one as disabled) and can only scroll to a mounted one. So the list opens, and reopens
 *   whenever the matching options change, on the option MUI is about to highlight (the selected
 *   one, else the first enabled one); before MUI handles a navigation key whose target is not
 *   mounted (Home, End, wrapping round, paging past disabled options) the listbox scrolls to it
 *   first; and when MUI highlights an unmounted option for any other reason, the listbox scrolls it
 *   into view and marks it focused itself.
 * - Grouping: pass `renderGroup={renderVirtualizedGroup}` alongside `groupBy`. MUI's default group
 *   markup nests a whole group inside one element, which would become a single row. Group headers
 *   scroll with the list (they are not sticky) and grouped options are not indented.
 */

// MUI's plain option: 6px padding either side of a 24px line, with a 48px minimum below `sm`.
const PLAIN_OPTION_HEIGHT = 36;
const PLAIN_OPTION_HEIGHT_XS = 48;
// ListSubheader's line height.
const GROUP_HEADER_HEIGHT = 48;
// MUI's listbox max-height is 40vh.
const MAX_HEIGHT_SHARE_OF_VIEWPORT = 0.4;
// Enough to keep the option PageUp/PageDown lands on (five away) mounted.
const OVERSCAN_COUNT = 5;
// How far MUI's PageUp/PageDown move the highlight.
const PAGE_SIZE = 5;
const FOCUSED_CLASS = 'Mui-focused';
const GROUP_HEADER_ATTRIBUTE = 'data-virtualized-group-header';

/** An option row led by MUI's checkbox with small icons (38px) plus the option padding. */
export const CHECKBOX_OPTION_HEIGHT = 50;

// MUI's listbox props (role, id, className, handlers) belong on the scrolling element, which
// react-window creates, so they reach it through context.
const OuterElementContext = createContext<HTMLAttributes<HTMLElement>>({});

const OuterElement = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function OuterElement(props, ref) {
  const listboxProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...listboxProps} style={{ ...props.style, ...listboxProps.style }} />;
});

const InnerElement = forwardRef<HTMLUListElement, HTMLAttributes<HTMLUListElement>>(function InnerElement(props, ref) {
  return <ul ref={ref} role='presentation' {...props} style={{ ...props.style, margin: 0, padding: 0 }} />;
});

const isGroupHeader = (item: ReactNode) => isValidElement<any>(item) && Boolean(item.props[GROUP_HEADER_ATTRIBUTE]);

const optionIndexOf = (item: ReactNode): number | undefined =>
  isValidElement<any>(item) ? item.props['data-option-index'] : undefined;

const isEnabledOption = (item: ReactNode) =>
  optionIndexOf(item) !== undefined && isValidElement<any>(item) && !item.props['aria-disabled'];

const isSelectedOption = (item: ReactNode) => isValidElement<any>(item) && item.props['aria-selected'] === true;

// Changes whenever the set or order of options changes (typing a filter), but not when an option
// is only ticked or unticked.
const optionsSignature = (items: ReactNode[]) => {
  let hash = 0;
  for (const item of items) {
    const key = isValidElement(item) ? String(item.key) : '';
    for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return `${items.length}:${hash}`;
};

const findRenderedOption = (scroller: HTMLElement | null, optionIndex: number) =>
  scroller?.querySelector(`[data-option-index="${optionIndex}"]`) ?? null;

// MUI points the input's aria-activedescendant at the highlighted option: `${inputId}-option-${index}`.
const highlightedOptionIndex = (input: HTMLElement): number | null => {
  const match = /-option-(\d+)$/.exec(input.getAttribute('aria-activedescendant') || '');
  return match ? Number(match[1]) : null;
};

// The option MUI's keydown handler for `key` will highlight, starting from option `highlighted`
// (-1 for none). Mirrors MUI's wrap-round and clamped paging, then its walk past disabled options.
// `enabled[i]` says whether option i can take the highlight. Null for keys MUI does not navigate
// with, or when no option can be highlighted.
const navigationTarget = (key: string, highlighted: number, enabled: boolean[]): number | null => {
  const max = enabled.length - 1;
  if (max < 0) return null;
  let start: number;
  let step: 1 | -1;
  switch (key) {
    case 'Home': start = 0; step = 1; break;
    case 'End': start = max; step = -1; break;
    case 'ArrowDown':
    case 'PageDown': {
      const diff = key === 'ArrowDown' ? 1 : PAGE_SIZE;
      const next = highlighted + diff;
      start = next > max ? (diff > 1 ? max : 0) : next;
      step = 1;
      break;
    }
    case 'ArrowUp':
    case 'PageUp': {
      const diff = key === 'ArrowUp' ? 1 : PAGE_SIZE;
      const next = highlighted - diff;
      start = next < 0 ? (diff > 1 ? 0 : max) : next;
      step = -1;
      break;
    }
    default: return null;
  }
  for (let i = 0, index = start; i <= max; i += 1, index = (index + step + enabled.length) % enabled.length) {
    if (enabled[index]) return index;
  }
  return null;
};

function Row({ data, index, style }: ListChildComponentProps<ReactNode[]>) {
  const item = data[index];
  const rowStyle: CSSProperties = { ...style, whiteSpace: 'nowrap' };
  if (!isValidElement<any>(item)) {
    return <div style={rowStyle}>{item}</div>;
  }
  return cloneElement(item, { style: { ...item.props.style, ...rowStyle } });
}

/** A virtualised listbox whose option rows are `rowHeight` px tall (MUI's plain option height if omitted). */
export function createVirtualizedListbox(rowHeight?: number) {
  return forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(function VirtualizedListbox(props, ref) {
    const { children, ...listboxProps } = props;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
    const optionHeight = rowHeight ?? (smUp ? PLAIN_OPTION_HEIGHT : PLAIN_OPTION_HEIGHT_XS);

    const items = Children.toArray(children);
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const sizeOf = (item: ReactNode) => (isGroupHeader(item) ? GROUP_HEADER_HEIGHT : optionHeight);

    const totalHeight = items.reduce<number>((sum, item) => sum + sizeOf(item), 0);
    const height = Math.min(totalHeight, Math.round(window.innerHeight * MAX_HEIGHT_SHARE_OF_VIEWPORT));

    // Open, and reopen when the matching options change, on the option MUI is about to highlight:
    // the selected one, else the first enabled one. It sits at the bottom edge, the way MUI scrolls
    // an option into view. react-window reads this only on mount, hence the key below.
    const selectedPosition = items.findIndex(isSelectedOption);
    const highlightPosition = selectedPosition !== -1 ? selectedPosition : items.findIndex(isEnabledOption);
    let initialScrollOffset = 0;
    if (highlightPosition > 0) {
      const bottom = items.slice(0, highlightPosition + 1).reduce<number>((sum, item) => sum + sizeOf(item), 0);
      initialScrollOffset = Math.max(0, bottom - height);
    }
    const listKey = optionsSignature(items);

    const listRef = useRef<VariableSizeList>(null);
    const outerRef = useRef<HTMLDivElement>(null);
    const pendingFocusRef = useRef<number | null>(null);

    // react-window caches row sizes. A change of options remounts the list (listKey); a plain row
    // height change (crossing the `sm` breakpoint) does not, so reset the cache for that.
    useEffect(() => {
      listRef.current?.resetAfterIndex(0, true);
    }, [optionHeight]);

    // Called by react-window after it mounts a new range of rows.
    const focusPendingOption = () => {
      const optionIndex = pendingFocusRef.current;
      const option = optionIndex === null ? null : findRenderedOption(outerRef.current, optionIndex);
      if (!option) return;
      pendingFocusRef.current = null;
      option.classList.add(FOCUSED_CLASS);
    };

    const listboxId = typeof listboxProps.id === 'string' ? listboxProps.id : undefined;
    useEffect(() => {
      // MUI sets aria-controls to the listbox's id only on the input whose popup is open, so this
      // finds the right input even where a page renders two Autocompletes with the same id.
      const input = listboxId
        ? document.querySelector<HTMLElement>(`[aria-controls="${CSS.escape(listboxId)}"]`)
        : null;
      if (!input) return undefined;
      const positionOf = (optionIndex: number) =>
        itemsRef.current.findIndex((item) => optionIndexOf(item) === optionIndex);
      // MUI leaves Home/End to the text cursor on a freeSolo Autocomplete, which is the one that
      // shows no popup indicator.
      const muiHandlesHomeEnd = !!input.closest('.MuiAutocomplete-hasPopupIcon');

      // Runs before MUI's own keydown handler (this listener is on the input itself; React's sit on
      // the root), so the option MUI is about to move to is mounted by the time it looks for it.
      const mountNavigationTarget = (event: KeyboardEvent) => {
        const list = listRef.current;
        if (!list || ((event.key === 'Home' || event.key === 'End') && !muiHandlesHomeEnd)) return;
        const enabled: boolean[] = [];
        const positions: number[] = [];
        itemsRef.current.forEach((item, position) => {
          const optionIndex = optionIndexOf(item);
          if (optionIndex === undefined) return;
          enabled[optionIndex] = isEnabledOption(item);
          positions[optionIndex] = position;
        });
        const target = navigationTarget(event.key, highlightedOptionIndex(input) ?? -1, enabled);
        if (target === null || findRenderedOption(outerRef.current, target)) return;
        flushSync(() => list.scrollToItem(positions[target]));
      };

      // Follow a highlight MUI set on an option that is not mounted (a changed filter, say).
      const followHighlight = () => {
        pendingFocusRef.current = null;
        const optionIndex = highlightedOptionIndex(input);
        if (optionIndex === null) return;
        // Mounted: MUI has already focused it and scrolled to it.
        if (findRenderedOption(outerRef.current, optionIndex)) return;
        const position = positionOf(optionIndex);
        if (position === -1) return;
        pendingFocusRef.current = optionIndex;
        listRef.current?.scrollToItem(position);
      };

      followHighlight();
      const observer = new MutationObserver(followHighlight);
      observer.observe(input, { attributes: true, attributeFilter: ['aria-activedescendant'] });
      input.addEventListener('keydown', mountNavigationTarget);
      return () => {
        observer.disconnect();
        input.removeEventListener('keydown', mountNavigationTarget);
      };
    }, [listboxId]);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={listboxProps}>
          <VariableSizeList
            key={listKey}
            ref={listRef}
            outerRef={outerRef}
            height={height}
            width='100%'
            itemCount={items.length}
            itemData={items}
            itemSize={(position) => sizeOf(items[position])}
            estimatedItemSize={optionHeight}
            initialScrollOffset={initialScrollOffset}
            overscanCount={OVERSCAN_COUNT}
            outerElementType={OuterElement}
            innerElementType={InnerElement}
            onItemsRendered={focusPendingOption}
          >
            {Row}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });
}

/** For one-line options. */
export const VirtualizedListbox = createVirtualizedListbox();

/** For options led by a checkbox with small icons (multi-selects). */
export const CheckboxVirtualizedListbox = createVirtualizedListbox(CHECKBOX_OPTION_HEIGHT);

/**
 * renderGroup for a grouped Autocomplete using a virtualised listbox: the group header becomes a
 * row of its own, followed by the group's options as ordinary rows.
 */
export const renderVirtualizedGroup = (params: AutocompleteRenderGroupParams): ReactNode => [
  <ListSubheader key={params.key} component='li' className='MuiAutocomplete-groupLabel' {...{ [GROUP_HEADER_ATTRIBUTE]: true }}>
    {params.group}
  </ListSubheader>,
  params.children,
];
